import React, { createContext, useState, useEffect } from "react";
import apiClient from "@shared/config/axiosConfig";
import {
  AUTH_STATE_CHANGED_EVENT,
  isAuthenticated as checkAuth,
  clearAuthData,
} from "../../../utils/authStorage";
import { emitSessionExpired } from "../../../components/SessionExpiredModal/SessionExpiredModal";
import {
  disconnectSocket,
  reconnectSocket,
} from "@shared/config/socketClient";

const AuthContext = createContext();

// Variable global para evitar múltiples refreshes simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, success = false) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

// Interceptor optimizado para refresh automático con cookies httpOnly
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Solo manejar 401s que no sean del endpoint de refresh ni de auth
    const authRoutes = [
      "/login",
      "/google-login",
      "/signup",
      "/refresh-access-token",
    ];
    const isAuthRoute = authRoutes.some((route) =>
      originalRequest.url.includes(route),
    );

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute &&
      !originalRequest.skipSessionExpired
    ) {
      if (isRefreshing) {
        // Si ya se está refrescando, agregar a la cola
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Las cookies se actualizan automáticamente, solo reintentar
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Verificar que hay sesión activa antes de intentar refresh
      if (!checkAuth()) {
        processQueue(error);
        isRefreshing = false;
        emitSessionExpired("No se encontró una sesión activa.");
        return Promise.reject(error);
      }

      try {
        // El refresh token se envía automáticamente via cookie httpOnly
        const response = await apiClient.post(
          "/refresh-access-token",
          {},
          {
            skipErrorLog: true,
            skipSessionExpired: true,
          },
        );

        if (response.status === 200) {
          // Las cookies se actualizan automáticamente en la respuesta
          processQueue(null, true);

          // Reconectar socket si estaba desconectado (token expirado al conectar)
          reconnectSocket();

          // Reintentar la solicitud original (las cookies ya están actualizadas)
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError);

        // La sesión ya no es recuperable: evitar que los metadatos locales
        // mantengan la UI y el socket en un estado falsamente autenticado.
        clearAuthData();
        disconnectSocket();

        // El refresh falló: la sesión ya no es recuperable
        const msg = (
          refreshError.response?.data?.message ||
          refreshError.response?.data?.error ||
          ""
        ).toLowerCase();

        const reason =
          msg.includes("expirado") || msg.includes("expired")
            ? "Tu sesión ha expirado completamente."
            : msg.includes("inválido") || msg.includes("invalid")
              ? "Tu token de sesión no es válido."
              : msg.includes("no se proporcionó")
                ? "No se encontró un token de sesión."
                : "Tu sesión ya no es válida. Por favor, inicia sesión nuevamente.";

        emitSessionExpired(reason);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => checkAuth());

  // Escuchar cambios en el estado de autenticación
  useEffect(() => {
    const handleStorageChange = () => {
      setIsLoggedIn(checkAuth());
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(AUTH_STATE_CHANGED_EVENT, handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ isLoggedIn, setIsLoggedIn }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
